/**
 * ScatterPlotPreview Component
 * visx implementation for scatter plots with multiple categories
 */

import { useMemo } from 'react';
import { scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { Group } from '@visx/group';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import ChartLegend from './ChartLegend';
import {
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  getXAxisBaselineNudge,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  niceDomain,
  LEGEND_SPACING, legendTopTranslateY, legendTopSpace,
  computeLegendRows,
  formatCompactNumber,
  getCompactTickFormatter,
} from '../../utils/chartLayout';
import { getSeriesMarkerSvg } from '../../utils/accessibility';
import { getColors } from '../../charts/shared/palettes';

function renderMarkerShape({ shape, x, y, size, fill, stroke, opacity = 1 }) {
  const s = size;
  const props = { fill, fillOpacity: opacity, stroke, strokeWidth: 1 };
  if (shape === 'square') return <rect x={x - s} y={y - s} width={s * 2} height={s * 2} {...props} />;
  if (shape === 'triangle') return <polygon points={`${x},${y - s} ${x - s},${y + s} ${x + s},${y + s}`} {...props} />;
  if (shape === 'diamond') return <polygon points={`${x},${y - s} ${x - s},${y} ${x},${y + s} ${x + s},${y}`} {...props} />;
  if (shape === 'triangleDown') return <polygon points={`${x - s},${y - s} ${x + s},${y - s} ${x},${y + s}`} {...props} />;
  if (shape === 'plus') {
    const t = s * 0.35;
    return <polygon points={`${x - t},${y - s} ${x + t},${y - s} ${x + t},${y - t} ${x + s},${y - t} ${x + s},${y + t} ${x + t},${y + t} ${x + t},${y + s} ${x - t},${y + s} ${x - t},${y + t} ${x - s},${y + t} ${x - s},${y - t} ${x - t},${y - t}`} {...props} />;
  }
  if (shape === 'cross') {
    const a = s * 0.7, t = s * 0.25;
    return <polygon points={`${x},${y - t} ${x + a - t},${y - a} ${x + a},${y - a + t} ${x + t},${y} ${x + a},${y + a - t} ${x + a - t},${y + a} ${x},${y + t} ${x - a + t},${y + a} ${x - a},${y + a - t} ${x - t},${y} ${x - a},${y - a + t} ${x - a + t},${y - a}`} {...props} />;
  }
  if (shape === 'hexagon') {
    const pts = [0, 1, 2, 3, 4, 5].map(i => { const a = Math.PI / 3 * i - Math.PI / 6; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'pentagon') {
    const pts = [0, 1, 2, 3, 4].map(i => { const a = (2 * Math.PI / 5) * i - Math.PI / 2; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'star') {
    const pts = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => { const a = (Math.PI / 5) * i - Math.PI / 2; const r = i % 2 === 0 ? s : s * 0.45; return `${x + r * Math.cos(a)},${y + r * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  return <circle cx={x} cy={y} r={s} {...props} />;
}

function ScatterPlotPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  const data = (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0)
    ? transformData(userData, mapping, chartType.id)
    : chartType.sampleData;

  // Group data by category
  const groupedData = useMemo(() => {
    const groups = {};
    data.forEach(point => {
      if (!groups[point.category]) groups[point.category] = [];
      groups[point.category].push(point);
    });
    return groups;
  }, [data]);

  // Colors per category (with user override support)
  const categoryColors = useMemo(() => {
    const colors = getColors(config, Object.keys(groupedData).length, theme);
    const overrides = config.colorOverrides || {};
    const colorMap = {};
    Object.keys(groupedData).forEach((cat, idx) => {
      colorMap[cat] = overrides[cat] || colors[idx % colors.length];
    });
    return colorMap;
  }, [config, groupedData, theme]);

  // Domains
  const xDomain = useMemo(() => {
    const xValues = data.map(d => d.x);
    return niceDomain(xValues, { padding: 0.05, minOverride: config.xMin, maxOverride: config.xMax });
  }, [data, config.xMin, config.xMax]);

  const yDomain = useMemo(() => {
    const yValues = data.map(d => d.y);
    return niceDomain(yValues, { padding: 0.05, minOverride: config.yMin, maxOverride: config.yMax });
  }, [data, config.yMin, config.yMax]);

  const yTicks = useMemo(
    () => scaleLinear({ domain: yDomain, range: [100, 0] }).ticks(),
    [yDomain],
  );

  // Defined early so estimatedLegendRows can be computed before computeChartLayout
  const categories = Object.keys(groupedData);
  const categoryStyles = useMemo(() => {
    const styleMap = {};
    categories.forEach((category, idx) => {
      styleMap[category] = getSeriesMarkerSvg(idx, config.useSeriesStyles);
    });
    return styleMap;
  }, [categories, config.useSeriesStyles]);
  const legendItems = useMemo(() => categories.map((cat) => ({
    key: cat,
    label: cat,
    color: categoryColors[cat],
    type: 'marker',
    markerShape: categoryStyles[cat] || 'circle',
  })), [categories, categoryColors, categoryStyles]);

  const legendMarkerScale = theme.legendMarkerSize != null ? theme.legendMarkerSize / LEGEND_SPACING.rectSize : 1;
  const estimatedLegendRows = useMemo(() => {
    const { rowCount } = computeLegendRows(legendItems, Math.max(120, width - 160), theme.legendSize ?? 12, legendMarkerScale);
    return rowCount;
  }, [legendItems, width, theme.legendSize, legendMarkerScale]);

  const { margin, innerWidth, innerHeight } = computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    yTickValues: yTicks,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'vertical',
    baseRight: 80,
    baseBottom: 70,
    legend: {
      show: !!config.showLegend,
      position: config.legendPosition || 'top',
      rightSpace: 70,
      bottomSpace: 80 + (estimatedLegendRows - 1) * LEGEND_SPACING.rowHeight,
      topSpace: legendTopSpace(estimatedLegendRows),
    },
  });
  const marginTop = margin.top;
  const marginLeft = margin.left;
  const marginRight = margin.right;
  const spacingSpec = getAxisSpacingSpec(theme);
  const xTickBaselineNudgeNumeric = getXAxisBaselineNudge({ tickType: 'numeric', spacingSpec });
  const { xTickGap, yTickGap, yTickBaselineNudge, xTickLength, yTickLength } = spacingSpec;
  const xTicksForLayout = useMemo(
    () => scaleLinear({ domain: xDomain, range: [0, 100] }).ticks(5).map((v) => String(v)),
    [xDomain],
  );
  const axisDebugEnabled = isAxisDebugEnabled();
  const xLanes = computeAxisLanes({
    axisSide: 'bottom',
    labels: xTicksForLayout,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: xTickLength,
    spacingSpec: { ...spacingSpec, gapX: xTickGap },
    telemetryMeta: { chartId: 'scatter-plot', viewportWidth: width, viewportHeight: height },
  });
  const xAxisTitleY = innerHeight + xLanes.titleOffsetPx;
  const yLanes = computeAxisLanes({
    axisSide: 'left',
    labels: yTicks.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: yTickLength,
    availableMargin: margin.left,
    spacingSpec: { ...spacingSpec, gapY: yTickGap },
    telemetryMeta: { chartId: 'scatter-plot', viewportWidth: width, viewportHeight: height },
  });
  if (axisDebugEnabled) {
    reportAxisLaneClamp(xLanes);
    reportAxisLaneClamp(yLanes);
  }

  // Scales
  const xScale = useMemo(
    () => scaleLinear({ domain: xDomain, range: [0, innerWidth] }),
    [xDomain, innerWidth]
  );

  const yScale = useMemo(
    () => scaleLinear({ domain: yDomain, range: [innerHeight, 0] }),
    [yDomain, innerHeight]
  );

  // Convert markerSize (area in px², matching Recharts ZAxis range) to radius
  const markerRadius = Math.sqrt((config.markerSize || 100) / Math.PI);

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      <ChartTitle title={config.title} chartWidth={width} marginLeft={marginLeft} marginRight={marginRight} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} onMouseLeave={hideTooltip}>
        <Group left={marginLeft} top={marginTop}>

          {/* Grid */}
          {config.showGrid && (
            <>
              <GridRows
                scale={yScale}
                width={innerWidth}
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
                strokeDasharray="3 3"
              />
              <GridColumns
                scale={xScale}
                height={innerHeight}
                stroke={theme.gridColor}
                strokeOpacity={theme.gridAlpha}
                strokeDasharray="3 3"
              />
            </>
          )}

          {/* X Axis */}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={xTickLength}
            tickFormat={getCompactTickFormatter(xScale.domain()[1])}
            tickLabelProps={() => ({
              fill: theme.tickColor,
              fontSize: theme.tickSize ?? 13,
              textAnchor: 'middle',
              dy: xTickBaselineNudgeNumeric,
              fontFamily: theme.fontNumber || theme.fontBody,
            })}
          />

          {/* Y Axis */}
          <AxisLeft
            scale={yScale}
            stroke={theme.axisColor}
            strokeWidth={theme.spineWidth}
            tickStroke={theme.axisColor}
            tickLength={yTickLength}
            tickFormat={getCompactTickFormatter(yScale.domain()[1])}
            tickLabelProps={() => ({
              fill: theme.tickColor,
              fontSize: theme.tickSize ?? 13,
              textAnchor: 'end',
              dx: -yTickGap,
              dy: yTickBaselineNudge,
              fontFamily: theme.fontNumber || theme.fontBody,
            })}
          />

          {/* Data points (rendered after axes so markers appear on top) */}
          {Object.entries(groupedData).map(([category, points]) =>
            points.map((point, i) => {
              const px = xScale(point.x);
              const py = yScale(point.y);
              const sl = config.scatterLabels;
              const dec = config.valueDecimals ?? 1;
              const labelText = sl === 'name' ? point.category
                : sl === 'x' ? formatCompactNumber(point.x, dec)
                : sl === 'y' ? formatCompactNumber(point.y, dec)
                : sl === 'xy' ? `(${formatCompactNumber(point.x, dec)}, ${formatCompactNumber(point.y, dec)})`
                : null;
              return (
                <g
                  key={`${category}-${i}`}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => showTip(e, `(${formatCompactNumber(point.x, 1)}, ${formatCompactNumber(point.y, 1)})`)}
                >
                  {renderMarkerShape({
                    shape: categoryStyles[category] || 'circle',
                    x: px,
                    y: py,
                    size: markerRadius,
                    fill: categoryColors[category],
                    stroke: theme.axisColor,
                    opacity: config.markerAlpha || 1.0,
                  })}
                  {labelText && (
                    <text
                      x={px + markerRadius + 4}
                      y={py - 4}
                      fontSize={theme.valueLabelSize ?? 11}
                      fontFamily={theme.fontLabel || theme.fontBody}
                      fill={theme.tickColor}
                      textAnchor="start"
                      pointerEvents="none"
                    >
                      {labelText}
                    </text>
                  )}
                </g>
              );
            })
          )}

          {/* Axis labels — manual rendering for consistent spacing */}
          <text
            x={-innerHeight / 2}
            y={-yLanes.titleOffsetPx}
            transform="rotate(-90)"
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fontFamily={theme.fontLabel || theme.fontBody}
            fill={theme.labelColor}
          >
            {config.ylabel || ''}
          </text>
          <text
            x={innerWidth / 2}
            y={xAxisTitleY}
            textAnchor="middle"
            fontSize={theme.labelSize ?? 15}
            fontFamily={theme.fontLabel || theme.fontBody}
            fill={theme.labelColor}
          >
            {config.xlabel || ''}
          </text>
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: yLanes.tickLabelOffsetPx,
                  titleOffsetPx: yLanes.titleOffsetPx,
                  clamped: yLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}

          {/* Legend – Top */}
          {config.showLegend && config.legendPosition === 'top' && (
            <ChartLegend
              items={legendItems}
              innerWidth={innerWidth}
              theme={theme}
              position="top"
              transform={`translate(0, ${legendTopTranslateY(estimatedLegendRows, width, height)})`}
            />
          )}

          {/* Legend – Bottom */}
          {config.showLegend && config.legendPosition === 'bottom' && (
            <ChartLegend
              items={legendItems}
              innerWidth={innerWidth}
              theme={theme}
              position="bottom"
              transform={`translate(0, ${innerHeight + LEGEND_SPACING.bottomGap})`}
            />
          )}
        </Group>

        {/* Legend – Right (outside the plot group) */}
        {config.showLegend && config.legendPosition === 'right' && (
          <ChartLegend
            items={legendItems}
            innerWidth={innerWidth}
            theme={theme}
            position="right"
            transform={`translate(${marginLeft + innerWidth + LEGEND_SPACING.rightGap}, ${marginTop + innerHeight / 2 - (categories.length * 12)})`}
          />
        )}
      </svg>
      <Tip />
    </div>
  );
}

export default ScatterPlotPreview;
